/**
 * useReportJob - Polling hook for platform report background jobs
 *
 * Features:
 * - Submit job and poll for completion
 * - Load cached results from URL ?job= parameter
 * - Update URL when job completes (bookmarkable)
 * - List recent completed jobs for dropdown
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { platformReportsService, PlatformReportJob, RecentJob, ReportFilters } from '../services/platformReportsService'

const POLL_INTERVAL = 500 // 500ms for responsive progress updates

interface UseReportJobReturn {
  job: PlatformReportJob | null
  submit: (filters?: ReportFilters) => Promise<void>
  reset: () => void
  isPolling: boolean
  recentJobs: RecentJob[]
  selectPastJob: (jobId: string) => void
}

export function useReportJob(reportType: string): UseReportJobReturn {
  const [job, setJob] = useState<PlatformReportJob | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([])
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const urlJobId = searchParams.get('job')

  // Load from URL param on mount or when ?job= changes (cached result)
  useEffect(() => {
    if (urlJobId) {
      loadJob(urlJobId)
    }
    // Also fetch recent jobs for dropdown
    loadRecentJobs()

    // Cleanup on unmount or before re-running on job change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [reportType, urlJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadJob = async (jobId: string) => {
    // Showing a cached result; stop any orphan poll for a different job.
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
      setIsPolling(false)
    }
    try {
      const status = await platformReportsService.getJobStatus(jobId)
      setJob(status)
    } catch (error) {
      console.error('Failed to load job:', error)
    }
  }

  const loadRecentJobs = async () => {
    try {
      const jobs = await platformReportsService.listRecentJobs(reportType)
      setRecentJobs(jobs)
    } catch (error) {
      console.error('Failed to load recent jobs:', error)
    }
  }

  const submit = useCallback(async (filters?: ReportFilters) => {
    try {
      // Set optimistic running state immediately for responsive UI
      setJob({
        job_id: '',
        report_type: reportType,
        status: 'running',
        progress_percentage: 0,
        progress_message: 'Submitting...',
      } as PlatformReportJob)
      setIsPolling(true)

      const { job_id } = await platformReportsService.submitJob(reportType, filters)

      // Update with real job_id and start progress
      setJob({
        job_id,
        report_type: reportType,
        status: 'running',
        progress_percentage: 5,
        progress_message: 'Starting report execution...',
      } as PlatformReportJob)

      // Start polling
      intervalRef.current = setInterval(async () => {
        try {
          const status = await platformReportsService.getJobStatus(job_id)
          setJob(status)

          if (status.status === 'completed' || status.status === 'failed') {
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
              intervalRef.current = null
            }
            setIsPolling(false)

            // Update URL with job_id for caching/bookmarking
            if (status.status === 'completed') {
              navigate(`?job=${job_id}`, { replace: true })
              loadRecentJobs() // Refresh recent jobs list
            }
          }
        } catch (error) {
          console.error('Failed to poll job status:', error)
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          setIsPolling(false)
          setJob((prev) => prev ? { ...prev, status: 'failed', error_message: 'Failed to check job status' } : null)
        }
      }, POLL_INTERVAL)

    } catch (error) {
      console.error('Failed to submit job:', error)
      setJob({ status: 'failed', error_message: 'Failed to submit job' } as PlatformReportJob)
      setIsPolling(false)
    }
  }, [reportType, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback(() => {
    setJob(null)
    setIsPolling(false)
    navigate('', { replace: true }) // Clear job param from URL
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [navigate])

  const selectPastJob = useCallback((jobId: string) => {
    navigate(`?job=${jobId}`, { replace: true })
    loadJob(jobId)
  }, [navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  return { job, submit, reset, isPolling, recentJobs, selectPastJob }
}

export default useReportJob
